import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import {
  Star, Search, Shield, Plus, Edit, Trash2, Eye, EyeOff, Upload,
  Loader2, GripVertical, Palette, Layout, Grid, Settings, Image,
  ArrowUp, ArrowDown, Check, X, Move, Sparkles, Award, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price || 0);
};

// Predefined badge colors
const BADGE_COLORS = [
  { name: 'Amber', value: '#f59e0b', bg: 'bg-amber-500' },
  { name: 'Green', value: '#22c55e', bg: 'bg-green-500' },
  { name: 'Blue', value: '#3b82f6', bg: 'bg-blue-500' },
  { name: 'Red', value: '#ef4444', bg: 'bg-red-500' },
  { name: 'Purple', value: '#8b5cf6', bg: 'bg-purple-500' },
  { name: 'Pink', value: '#ec4899', bg: 'bg-pink-500' },
  { name: 'Teal', value: '#14b8a6', bg: 'bg-teal-500' },
  { name: 'Orange', value: '#f97316', bg: 'bg-orange-500' },
];

// Predefined badge texts
const BADGE_TEXTS = [
  'Best Seller', 'New', 'Popular', 'Limited', 'Sale', 'Top Rated', 
  'Exclusive', 'Hot Deal', 'Staff Pick', 'Trending'
];

// Card layout options
const LAYOUT_OPTIONS = [
  { value: 'grid-3', label: '3 Columns Grid', icon: Grid },
  { value: 'grid-4', label: '4 Columns Grid', icon: Grid },
  { value: 'grid-2', label: '2 Columns Grid', icon: Grid },
  { value: 'list', label: 'List View', icon: Layout },
];

// Card size options
const SIZE_OPTIONS = [
  { value: 'compact', label: 'Compact', height: '280px' },
  { value: 'standard', label: 'Standard', height: '340px' },
  { value: 'large', label: 'Large', height: '420px' },
];

export default function CustomizeTab({ tours = [], onRefresh }) {
  const [activeSubTab, setActiveSubTab] = useState('featured');
  const [loading, setLoading] = useState(true);
  
  // Featured Tours State
  const [selectedTours, setSelectedTours] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Quick Searches State
  const [searches, setSearches] = useState([]);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [editingSearch, setEditingSearch] = useState(null);
  const [searchFormData, setSearchFormData] = useState({
    name: '',
    query: '',
    category: 'all',
    is_active: true,
    sort_order: 0,
  });
  
  // Trust Badges State
  const [badges, setBadges] = useState([]);
  const [showBadgeDialog, setShowBadgeDialog] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [badgeFormData, setBadgeFormData] = useState({
    name: '',
    image_url: '',
    is_active: true,
  });
  
  // Product Listing Customization State
  const [listingSettings, setListingSettings] = useState({
    layout: 'grid-3',
    cardSize: 'standard',
    showPrices: true,
    showRatings: true,
    showBadges: true,
    cardBorderRadius: 12,
    cardShadow: 'medium',
    imageAspectRatio: '4:3',
    priceColor: '#22c55e',
    badgePosition: 'top-left',
    hoverEffect: 'scale',
    maxFeaturedTours: 2, // Default to 2, can be 1-5
  });
  const [showListingPreview, setShowListingPreview] = useState(false);
  
  // Tour Badge Customization
  const [tourBadges, setTourBadges] = useState({});
  const [editingTourBadge, setEditingTourBadge] = useState(null);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'marine', label: 'Marine' },
    { value: 'food', label: 'Food & Culture' },
    { value: 'night', label: 'Night Tours' },
  ];

  useEffect(() => {
    loadAllData();
  }, [tours]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load listing settings FIRST to get maxFeaturedTours
      let maxFeatured = 2; // default
      try {
        const settingsRes = await adminAPI.getSettings();
        if (settingsRes.data?.listing_settings) {
          const loadedSettings = { ...listingSettings, ...settingsRes.data.listing_settings };
          setListingSettings(loadedSettings);
          maxFeatured = loadedSettings.maxFeaturedTours || 2;
        }
      } catch (e) {
        // No listing settings found, using defaults - this is expected for new setups
      }
      
      // Load featured tours with the correct max
      const featuredTours = tours.filter(t => t.is_featured);
      setSelectedTours(featuredTours.slice(0, maxFeatured));
      
      // Load tour badges
      const badgeMap = {};
      tours.forEach(t => {
        if (t.featured_badge) {
          badgeMap[t.id] = {
            text: t.featured_badge,
            color: t.featured_badge_color || '#f59e0b'
          };
        }
      });
      setTourBadges(badgeMap);
      
      // Load quick searches
      const searchesRes = await adminAPI.getAllQuickSearches();
      setSearches(searchesRes.data || []);
      
      // Load trust badges
      const badgesRes = await adminAPI.getAllBadges();
      setBadges(badgesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load customization data');
    } finally {
      setLoading(false);
    }
  };

  // ========== Featured Tours Handlers ==========
  const handleToggleFeatured = async (tour) => {
    const isCurrentlySelected = selectedTours.some(t => t.id === tour.id);
    const maxFeatured = listingSettings.maxFeaturedTours || 2;
    
    if (isCurrentlySelected) {
      setSelectedTours(selectedTours.filter(t => t.id !== tour.id));
      try {
        await adminAPI.updateTour(tour.id, { is_featured: false });
        toast.success(`${tour.title} removed from featured`);
        onRefresh();
      } catch (error) {
        toast.error('Failed to update tour');
      }
    } else {
      if (selectedTours.length >= maxFeatured) {
        toast.error(`Maximum ${maxFeatured} featured tours allowed. Remove one first.`);
        return;
      }
      setSelectedTours([...selectedTours, tour]);
      try {
        await adminAPI.updateTour(tour.id, { is_featured: true });
        toast.success(`${tour.title} added to featured`);
        onRefresh();
      } catch (error) {
        toast.error('Failed to update tour');
      }
    }
  };

  const handleUpdateTourBadge = async (tourId, badge, color) => {
    try {
      await adminAPI.updateTour(tourId, { 
        featured_badge: badge,
        featured_badge_color: color
      });
      setTourBadges(prev => ({
        ...prev,
        [tourId]: { text: badge, color }
      }));
      toast.success('Badge updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update badge');
    }
  };

  // ========== Quick Searches Handlers ==========
  const handleSearchSubmit = async () => {
    if (!searchFormData.name || !searchFormData.query) {
      toast.error('Please fill in name and search query');
      return;
    }

    try {
      if (editingSearch) {
        await adminAPI.updateQuickSearch(editingSearch.id, searchFormData);
        toast.success('Quick search updated!');
      } else {
        await adminAPI.createQuickSearch(searchFormData);
        toast.success('Quick search created!');
      }
      setShowSearchDialog(false);
      setEditingSearch(null);
      setSearchFormData({ name: '', query: '', category: 'all', is_active: true, sort_order: 0 });
      loadAllData();
    } catch (error) {
      toast.error('Failed to save quick search');
    }
  };

  const handleDeleteSearch = async (searchId) => {
    if (!confirm('Delete this quick search?')) return;
    try {
      await adminAPI.deleteQuickSearch(searchId);
      toast.success('Quick search deleted!');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete quick search');
    }
  };

  // ========== Trust Badges Handlers ==========
  const handleBadgeImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_data: base64 }),
        });
        
        if (!response.ok) throw new Error('Upload failed');
        
        const data = await response.json();
        const imageUrl = `${API_URL}${data.url}`;
        setBadgeFormData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success('Image uploaded!');
        setUploading(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
      setUploading(false);
    }
  };

  const handleBadgeSubmit = async () => {
    if (!badgeFormData.name || !badgeFormData.image_url) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      if (editingBadge) {
        await adminAPI.updateBadge(editingBadge.id, badgeFormData);
        toast.success('Badge updated!');
      } else {
        await adminAPI.createBadge(badgeFormData);
        toast.success('Badge created!');
      }
      setShowBadgeDialog(false);
      setEditingBadge(null);
      setBadgeFormData({ name: '', image_url: '', is_active: true });
      loadAllData();
    } catch (error) {
      toast.error('Failed to save badge');
    }
  };

  const handleDeleteBadge = async (badgeId) => {
    if (!confirm('Delete this badge?')) return;
    try {
      await adminAPI.deleteBadge(badgeId);
      toast.success('Badge deleted!');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete badge');
    }
  };

  // ========== Listing Settings Handlers ==========
  const handleSaveListingSettings = async () => {
    try {
      await adminAPI.updateSettings({ listing_settings: listingSettings });
      toast.success('Listing settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const filteredTours = tours.filter(t => 
    t.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="customize-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold">Customize</h1>
          <p className="text-muted-foreground mt-1">Manage featured content, badges, and front page appearance</p>
        </div>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="featured" className="gap-2">
            <Star className="h-4 w-4" />
            Featured
          </TabsTrigger>
          <TabsTrigger value="searches" className="gap-2">
            <Search className="h-4 w-4" />
            Quick Searches
          </TabsTrigger>
          <TabsTrigger value="badges" className="gap-2">
            <Shield className="h-4 w-4" />
            Trust Badges
          </TabsTrigger>
          <TabsTrigger value="listings" className="gap-2">
            <Layout className="h-4 w-4" />
            Product Listings
          </TabsTrigger>
        </TabsList>

        {/* ========== FEATURED TOURS TAB ========== */}
        <TabsContent value="featured">
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                Currently Featured ({selectedTours.length}/{listingSettings.maxFeaturedTours || 2})
              </h2>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Max Featured:</Label>
                <Select 
                  value={String(listingSettings.maxFeaturedTours || 2)}
                  onValueChange={async (val) => {
                    const newMax = parseInt(val);
                    setListingSettings(prev => ({ ...prev, maxFeaturedTours: newMax }));
                    // If current selection exceeds new max, trim it
                    if (selectedTours.length > newMax) {
                      const toursToRemove = selectedTours.slice(newMax);
                      for (const tour of toursToRemove) {
                        try {
                          await adminAPI.updateTour(tour.id, { is_featured: false });
                        } catch (e) {
                          console.error('Failed to remove featured status', e);
                        }
                      }
                      setSelectedTours(selectedTours.slice(0, newMax));
                      toast.info(`Reduced to ${newMax} featured tours`);
                      onRefresh();
                    }
                    // Save to backend settings
                    try {
                      await adminAPI.updateSettings({
                        listing_settings: { ...listingSettings, maxFeaturedTours: newMax }
                      });
                    } catch (e) {
                      console.error('Failed to save settings', e);
                    }
                  }}
                >
                  <SelectTrigger className="w-[80px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map(num => (
                      <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {selectedTours.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Star className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No tours featured yet. Select tours below to feature them.</p>
              </div>
            ) : (
              <div className={`grid gap-4 ${
                selectedTours.length === 1 ? 'grid-cols-1' :
                selectedTours.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                selectedTours.length <= 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }`}>
                {selectedTours.map((tour) => (
                  <div key={tour.id} className="relative border rounded-lg overflow-hidden bg-gradient-to-br from-amber-50 to-white">
                    <div className="flex gap-4 p-4">
                      <img 
                        src={tour.image_url || '/placeholder-tour.jpg'} 
                        alt={tour.title}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold">{tour.title}</h3>
                        <p className="text-sm text-muted-foreground">{formatPrice(tour.price)}</p>
                        
                        {/* Badge Customization */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <Select 
                            value={tourBadges[tour.id]?.text || 'none'} 
                            onValueChange={(v) => handleUpdateTourBadge(tour.id, v === 'none' ? '' : v, tourBadges[tour.id]?.color || '#f59e0b')}
                          >
                            <SelectTrigger className="w-[130px] h-8 text-xs">
                              <SelectValue placeholder="Add badge..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Badge</SelectItem>
                              {BADGE_TEXTS.map(text => (
                                <SelectItem key={text} value={text}>{text}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          {tourBadges[tour.id]?.text && (
                            <div className="flex gap-1">
                              {BADGE_COLORS.slice(0, 4).map(color => (
                                <button
                                  key={color.value}
                                  onClick={() => handleUpdateTourBadge(tour.id, tourBadges[tour.id].text, color.value)}
                                  className={`w-5 h-5 rounded-full border-2 ${
                                    tourBadges[tour.id]?.color === color.value ? 'border-black' : 'border-transparent'
                                  }`}
                                  style={{ backgroundColor: color.value }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleToggleFeatured(tour)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {tourBadges[tour.id]?.text && (
                      <div 
                        className="absolute top-2 left-2 px-2 py-1 text-xs font-bold text-white rounded"
                        style={{ backgroundColor: tourBadges[tour.id].color }}
                      >
                        {tourBadges[tour.id].text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* All Tours Selection */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Select Tours to Feature</h2>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tours..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto">
              {filteredTours.map((tour) => {
                const isSelected = selectedTours.some(t => t.id === tour.id);
                return (
                  <div 
                    key={tour.id}
                    onClick={() => handleToggleFeatured(tour)}
                    className={`relative border rounded-lg p-3 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200' 
                        : 'hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-amber-500 text-white rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    <div className="flex gap-3">
                      <img 
                        src={tour.image_url || '/placeholder-tour.jpg'} 
                        alt={tour.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div>
                        <h3 className="font-medium text-sm line-clamp-2">{tour.title}</h3>
                        <p className="text-sm text-green-600 font-medium">{formatPrice(tour.price)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* ========== QUICK SEARCHES TAB ========== */}
        <TabsContent value="searches">
          <div className="flex items-center justify-between mb-4">
            <p className="text-muted-foreground">Create shortcuts for popular search queries</p>
            <Button onClick={() => { setEditingSearch(null); setSearchFormData({ name: '', query: '', category: 'all', is_active: true, sort_order: 0 }); setShowSearchDialog(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Quick Search
            </Button>
          </div>

          {searches.length === 0 ? (
            <Card className="p-8 text-center">
              <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No quick searches yet</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searches.map((search) => (
                <Card key={search.id} className={`p-4 ${!search.is_active ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{search.name}</h3>
                        {!search.is_active && <Badge variant="outline">Hidden</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">Query: "{search.query}"</p>
                      {search.category && (
                        <Badge variant="secondary" className="mt-2">{search.category}</Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => {
                        setEditingSearch(search);
                        setSearchFormData(search);
                        setShowSearchDialog(true);
                      }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteSearch(search.id)} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Quick Search Dialog */}
          <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingSearch ? 'Edit Quick Search' : 'New Quick Search'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={searchFormData.name}
                    onChange={(e) => setSearchFormData({ ...searchFormData, name: e.target.value })}
                    placeholder="e.g., Beach Tours"
                  />
                </div>
                <div>
                  <Label>Search Query</Label>
                  <Input
                    value={searchFormData.query}
                    onChange={(e) => setSearchFormData({ ...searchFormData, query: e.target.value })}
                    placeholder="e.g., beach snorkel"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={searchFormData.category || 'all'} onValueChange={(v) => setSearchFormData({ ...searchFormData, category: v === 'all' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch checked={searchFormData.is_active} onCheckedChange={(v) => setSearchFormData({ ...searchFormData, is_active: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSearchDialog(false)}>Cancel</Button>
                <Button onClick={handleSearchSubmit}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ========== TRUST BADGES TAB ========== */}
        <TabsContent value="badges">
          <div className="flex items-center justify-between mb-4">
            <p className="text-muted-foreground">Trust badges displayed in the footer and checkout</p>
            <Button onClick={() => { setEditingBadge(null); setBadgeFormData({ name: '', image_url: '', is_active: true }); setShowBadgeDialog(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Badge
            </Button>
          </div>

          {badges.length === 0 ? (
            <Card className="p-8 text-center">
              <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No trust badges yet</p>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {badges.map((badge) => (
                <Card key={badge.id} className={`p-4 text-center ${!badge.is_active ? 'opacity-50' : ''}`}>
                  <img 
                    src={badge.image_url} 
                    alt={badge.name}
                    className="w-16 h-16 mx-auto object-contain mb-2"
                  />
                  <p className="text-sm font-medium truncate">{badge.name}</p>
                  <div className="flex justify-center gap-1 mt-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                      setEditingBadge(badge);
                      setBadgeFormData(badge);
                      setShowBadgeDialog(true);
                    }}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteBadge(badge.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Badge Dialog */}
          <Dialog open={showBadgeDialog} onOpenChange={setShowBadgeDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingBadge ? 'Edit Badge' : 'New Badge'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Badge Name</Label>
                  <Input
                    value={badgeFormData.name}
                    onChange={(e) => setBadgeFormData({ ...badgeFormData, name: e.target.value })}
                    placeholder="e.g., SSL Secured"
                  />
                </div>
                <div>
                  <Label>Badge Image</Label>
                  <div className="mt-2 flex items-center gap-4">
                    {badgeFormData.image_url && (
                      <img src={badgeFormData.image_url} alt="Preview" className="w-16 h-16 object-contain border rounded" />
                    )}
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                      Upload Image
                    </Button>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleBadgeImageUpload} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch checked={badgeFormData.is_active} onCheckedChange={(v) => setBadgeFormData({ ...badgeFormData, is_active: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBadgeDialog(false)}>Cancel</Button>
                <Button onClick={handleBadgeSubmit}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ========== PRODUCT LISTINGS TAB ========== */}
        <TabsContent value="listings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings Panel */}
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Grid className="h-4 w-4" />
                  Layout Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Grid Layout</Label>
                    <Select value={listingSettings.layout} onValueChange={(v) => setListingSettings({ ...listingSettings, layout: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LAYOUT_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Card Size</Label>
                    <Select value={listingSettings.cardSize} onValueChange={(v) => setListingSettings({ ...listingSettings, cardSize: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SIZE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Border Radius: {listingSettings.cardBorderRadius}px</Label>
                    <Slider
                      value={[listingSettings.cardBorderRadius]}
                      onValueChange={([v]) => setListingSettings({ ...listingSettings, cardBorderRadius: v })}
                      min={0}
                      max={24}
                      step={2}
                      className="mt-2"
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Style Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Card Shadow</Label>
                    <Select value={listingSettings.cardShadow} onValueChange={(v) => setListingSettings({ ...listingSettings, cardShadow: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Shadow</SelectItem>
                        <SelectItem value="small">Small</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="large">Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Hover Effect</Label>
                    <Select value={listingSettings.hoverEffect} onValueChange={(v) => setListingSettings({ ...listingSettings, hoverEffect: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="scale">Scale Up</SelectItem>
                        <SelectItem value="lift">Lift (Shadow)</SelectItem>
                        <SelectItem value="glow">Glow</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Price Color</Label>
                    <div className="flex gap-2 mt-2">
                      {['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#000000'].map(color => (
                        <button
                          key={color}
                          onClick={() => setListingSettings({ ...listingSettings, priceColor: color })}
                          className={`w-8 h-8 rounded-full border-2 ${
                            listingSettings.priceColor === color ? 'border-black ring-2 ring-offset-2' : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Badge Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Badge Position</Label>
                    <Select value={listingSettings.badgePosition} onValueChange={(v) => setListingSettings({ ...listingSettings, badgePosition: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="top-left">Top Left</SelectItem>
                        <SelectItem value="top-right">Top Right</SelectItem>
                        <SelectItem value="bottom-left">Bottom Left</SelectItem>
                        <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Show Badges</Label>
                    <Switch checked={listingSettings.showBadges} onCheckedChange={(v) => setListingSettings({ ...listingSettings, showBadges: v })} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Show Prices</Label>
                    <Switch checked={listingSettings.showPrices} onCheckedChange={(v) => setListingSettings({ ...listingSettings, showPrices: v })} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Show Ratings</Label>
                    <Switch checked={listingSettings.showRatings} onCheckedChange={(v) => setListingSettings({ ...listingSettings, showRatings: v })} />
                  </div>
                </div>
              </Card>

              <Button onClick={handleSaveListingSettings} className="w-full">
                <Check className="h-4 w-4 mr-2" />
                Save Listing Settings
              </Button>
            </div>

            {/* Preview Panel */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Live Preview
              </h3>
              <div 
                className={`grid gap-4 ${
                  listingSettings.layout === 'grid-4' ? 'grid-cols-4' :
                  listingSettings.layout === 'grid-2' ? 'grid-cols-2' :
                  listingSettings.layout === 'list' ? 'grid-cols-1' : 'grid-cols-3'
                }`}
              >
                {tours.slice(0, listingSettings.layout === 'list' ? 3 : 6).map((tour) => {
                  const shadowClass = 
                    listingSettings.cardShadow === 'small' ? 'shadow-sm' :
                    listingSettings.cardShadow === 'medium' ? 'shadow-md' :
                    listingSettings.cardShadow === 'large' ? 'shadow-lg' : '';
                  
                  const hoverClass = 
                    listingSettings.hoverEffect === 'scale' ? 'hover:scale-105' :
                    listingSettings.hoverEffect === 'lift' ? 'hover:shadow-xl hover:-translate-y-1' :
                    listingSettings.hoverEffect === 'glow' ? 'hover:ring-2 hover:ring-primary/50' : '';
                  
                  const cardHeight = SIZE_OPTIONS.find(s => s.value === listingSettings.cardSize)?.height || '340px';
                  
                  return (
                    <div
                      key={tour.id}
                      className={`relative overflow-hidden bg-white border transition-all duration-200 ${shadowClass} ${hoverClass}`}
                      style={{ 
                        borderRadius: `${listingSettings.cardBorderRadius}px`,
                        height: listingSettings.layout === 'list' ? 'auto' : cardHeight
                      }}
                    >
                      {listingSettings.showBadges && tourBadges[tour.id]?.text && (
                        <div 
                          className={`absolute z-10 px-2 py-1 text-xs font-bold text-white ${
                            listingSettings.badgePosition === 'top-left' ? 'top-2 left-2' :
                            listingSettings.badgePosition === 'top-right' ? 'top-2 right-2' :
                            listingSettings.badgePosition === 'bottom-left' ? 'bottom-2 left-2' : 'bottom-2 right-2'
                          }`}
                          style={{ 
                            backgroundColor: tourBadges[tour.id].color,
                            borderRadius: `${Math.min(listingSettings.cardBorderRadius, 8)}px`
                          }}
                        >
                          {tourBadges[tour.id].text}
                        </div>
                      )}
                      <div className={`${listingSettings.layout === 'list' ? 'flex gap-4' : ''}`}>
                        <img 
                          src={tour.image_url || '/placeholder-tour.jpg'} 
                          alt={tour.title}
                          className={`object-cover ${listingSettings.layout === 'list' ? 'w-32 h-24' : 'w-full h-32'}`}
                          style={{ borderRadius: listingSettings.layout === 'list' ? `${listingSettings.cardBorderRadius}px` : `${listingSettings.cardBorderRadius}px ${listingSettings.cardBorderRadius}px 0 0` }}
                        />
                        <div className="p-3">
                          <h4 className="font-medium text-sm line-clamp-2">{tour.title}</h4>
                          {listingSettings.showRatings && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                              <span className="text-xs text-muted-foreground">4.8</span>
                            </div>
                          )}
                          {listingSettings.showPrices && (
                            <p className="font-bold mt-2" style={{ color: listingSettings.priceColor }}>
                              {formatPrice(tour.price)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
